<template>
  <BubbleChart
    chart-id="revisions-bubble-chart"
    title="Post revisions - Bubble chart: average score for number of revisions"
    :chart-data="chartData"
    :options="options"
    :styles="{
      width: '100%',
      height: '100%',
    }"
  />
</template>

<script>
import { numberWithCommas } from '@/helpers/formatters';
import BubbleChart from '@/components/charts/BubbleChart.vue';
import { mapState } from 'pinia';
import { useUsage } from '@/stores/insights/usage';
import { useColors } from '@/composables/colors';

export default {
  name: 'RevisionsBubbleChart',
  components: {
    BubbleChart,
  },
  setup() {
    const {
      basicRed, blue500, green500, textDarkest,
    } = useColors();

    return {
      basicRed, blue500, green500, textDarkest,
    };
  },
  computed: {
    ...mapState(useUsage, ['postRevisionsScore']),
    formattedScoreData() {
      const labels = Array.from({ length: 21 }, (_, i) => (i === 20 ? `${i * 5}+` : (i * 5).toString()));
      const datasetsObj = {
        '0-60': [],
        '61-70': [],
        '71-84': [],
        '85-100+': [],
      };

      const revisions = this.postRevisionsScore || [];

      revisions.forEach((bubble) => {
        const { y } = bubble;
        if (y <= 60) {
          datasetsObj['0-60'].push(bubble);
        } else if (y >= 61 && y <= 70) {
          datasetsObj['61-70'].push(bubble);
        } else if (y >= 71 && y <= 84) {
          datasetsObj['71-84'].push(bubble);
        } else if (y >= 85) {
          datasetsObj['85-100+'].push(bubble);
        }
      });

      const keys = Object.keys(datasetsObj);
      const datasets = keys.map((key) => ({ label: key, data: datasetsObj[key] }));

      return { labels, datasets };
    },
    maxRadialValue() {
      let maxValue = 0;
      this.formattedScoreData?.datasets.forEach((dataset) => dataset.data.forEach((dataPoint) => {
        if (dataPoint.r > maxValue) maxValue = dataPoint.r;
      }));
      return maxValue;
    },
    minRadialValue() {
      let minValue;
      this.formattedScoreData?.datasets.forEach((dataset) => dataset.data.forEach((dataPoint) => {
        if (minValue === undefined) minValue = dataPoint.r;
        if (dataPoint.r > minValue) minValue = dataPoint.r;
      }));
      return minValue;
    },
    chartData() {
      const { basicRed, blue500, green500 } = this;
      const bubbleBackgroundColors = ['rgba(219, 67, 70, 0.5)', 'rgba(255, 106, 91, 0.5)', 'rgba(0, 149, 221, 0.5)', 'rgba(11, 184, 139, 0.5)'];
      const bubbleBorderColors = [basicRed, '#ff695b', blue500, green500];

      const formmatedDatasets = this.formattedScoreData?.datasets.map((dataset, i) => ({
        ...dataset,
        backgroundColor: bubbleBackgroundColors[i],
        borderColor: bubbleBorderColors[i],
        data: dataset.data.map(({ x, y, r }) => ({
          x: x > 100 ? 100 : x,
          y: y > 100 ? 100 : y,
          r: this.getRadius(r),
          originalRadius: r,
        })),
      }));
      return { ...this.formattedScoreData, datasets: formmatedDatasets };
    },
    options() {
      return {
        maintainAspectRatio: false,
        responsive: true,
        plugins: {
          legend: {
            display: false,
          },
          tooltip: {
            backgroundColor: 'white',
            bodyColor: this.textDarkest,
            bodyFont: {
              size: 14,
              family: 'Inter',
            },
            padding: {
              y: 12,
              x: 6,
            },
            callbacks: {
              label: (tooltipItem) => {
                const dataPoint = tooltipItem.dataset.data[tooltipItem.dataIndex] || '';
                return ` Jobs: ${numberWithCommas(dataPoint.originalRadius) || 0}`;
              },
            },
          },
        },
        scales: {
          y: {
            title: {
              display: true,
              text: 'Average Score',
            },
            ticks: {
              beginAtZero: true,
              maxTicksLimit: 6,
              padding: 10,
            },
            border: {
              display: false,
            },
            grid: {
              drawTicks: false,
              zeroLineWidth: 0.5,
            },
          },
          x: {
            title: {
              display: true,
              text: 'Number of Revisions',
            },
            ticks: {
              beginAtZero: true,
              maxTicksLimit: 11,
              padding: 10,
              callback(value) {
                return value >= 100 ? `${value}+` : value.toString();
              },
            },
            grid: {
              display: false,
            },
          },
        },
      };
    },
  },
  methods: {
    getRadius(r) {
      const maxRadius = 10;
      const minRadius = 2;

      const percentageOfMaxValue = r / this.maxRadialValue;
      return (percentageOfMaxValue * (maxRadius - minRadius)) + minRadius;
    },
  },
};
</script>
